/**
 * Firebase Storage Utilities for OilWellX
 * Helper functions for file uploads, downloads, and management
 */

import {
  ref,
  uploadBytes,
  uploadBytesResumable,
  getDownloadURL,
  deleteObject,
  listAll,
  getMetadata,
  updateMetadata,
  UploadTask,
  StorageReference
} from 'firebase/storage';
import { storage } from '../firebase/client';

export interface UploadProgress {
  progress: number;
  bytesTransferred: number;
  totalBytes: number;
}

export interface ProductionMediaUploadResult {
  downloadUrl: string;
  storagePath: string;
  metadata: Record<string, string>;
}

const PRODUCTION_FOLDER = 'production';
const PRODUCTION_PHOTOS_FOLDER = 'photos';
const PRODUCTION_VOICE_MEMOS_FOLDER = 'voice_memos';

type ProductionMediaFolder = typeof PRODUCTION_PHOTOS_FOLDER | typeof PRODUCTION_VOICE_MEMOS_FOLDER;

export function normalizeProductionDate(date: string | Date): string {
  if (date instanceof Date && !Number.isNaN(date.getTime())) {
    return date.toISOString().split('T')[0];
  }

  if (typeof date === 'string') {
    const trimmed = date.trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
      return trimmed;
    }

    const parsed = new Date(trimmed);
    if (!Number.isNaN(parsed.getTime())) {
      return parsed.toISOString().split('T')[0];
    }

    if (trimmed.length >= 10) {
      return trimmed.slice(0, 10);
    }
  }

  return new Date().toISOString().split('T')[0];
}

function buildProductionStoragePath(
  companyId: string,
  wellId: string,
  date: string | Date,
  folder?: ProductionMediaFolder
): string {
  const normalizedDate = normalizeProductionDate(date);
  const basePath = `companies/${companyId}/wells/${wellId}/${PRODUCTION_FOLDER}/${normalizedDate}`;
  return folder ? `${basePath}/${folder}` : basePath;
}

export function getProductionStorageBasePath(
  companyId: string,
  wellId: string,
  date: string | Date
): string {
  return buildProductionStoragePath(companyId, wellId, date);
}

export function getProductionPhotoStoragePath(
  companyId: string,
  wellId: string,
  date: string | Date
): string {
  return buildProductionStoragePath(companyId, wellId, date, PRODUCTION_PHOTOS_FOLDER);
}

export function getProductionVoiceMemoStoragePath(
  companyId: string,
  wellId: string,
  date: string | Date
): string {
  return buildProductionStoragePath(companyId, wellId, date, PRODUCTION_VOICE_MEMOS_FOLDER);
}

/**
 * Upload a file to Firebase Storage
 */
export async function uploadFile(
  path: string,
  file: File,
  metadata?: Record<string, any>,
  onProgress?: (progress: UploadProgress) => void
): Promise<string | null> {
  try {
    const storageRef = ref(storage, path);
    
    if (onProgress) {
      // Use resumable upload with progress tracking
      const uploadTask = uploadBytesResumable(storageRef, file, metadata);
      
      return new Promise((resolve, reject) => {
        uploadTask.on(
          'state_changed',
          (snapshot) => {
            const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
            onProgress({
              progress,
              bytesTransferred: snapshot.bytesTransferred,
              totalBytes: snapshot.totalBytes
            });
          },
          (error) => {
            console.error('Upload error:', error);
            reject(error);
          },
          async () => {
            const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
            resolve(downloadURL);
          }
        );
      });
    } else {
      // Simple upload without progress
      const snapshot = await uploadBytes(storageRef, file, metadata);
      return await getDownloadURL(snapshot.ref);
    }
  } catch (error) {
    console.error('Error uploading file:', error);
    return null;
  }
}

/**
 * Upload multiple files
 */
export async function uploadMultipleFiles(
  basePath: string,
  files: File[],
  onProgress?: (fileName: string, progress: UploadProgress) => void
): Promise<string[]> {
  const uploadPromises = files.map(file => {
    const filePath = `${basePath}/${file.name}`;
    return uploadFile(
      filePath,
      file,
      undefined,
      onProgress ? (progress) => onProgress(file.name, progress) : undefined
    );
  });
  
  const results = await Promise.all(uploadPromises);
  return results.filter(url => url !== null) as string[];
}

/**
 * Get download URL for a file
 */
export async function getFileURL(path: string): Promise<string | null> {
  try {
    const storageRef = ref(storage, path);
    return await getDownloadURL(storageRef);
  } catch (error) {
    console.error('Error getting file URL:', error);
    return null;
  }
}

/**
 * Delete a file
 */
export async function deleteFile(path: string): Promise<boolean> {
  try {
    const storageRef = ref(storage, path);
    await deleteObject(storageRef);
    return true;
  } catch (error) {
    console.error('Error deleting file:', error);
    return false;
  }
}

/**
 * Delete multiple files
 */
export async function deleteMultipleFiles(paths: string[]): Promise<boolean> {
  try {
    const deletePromises = paths.map(path => deleteFile(path));
    await Promise.all(deletePromises);
    return true;
  } catch (error) {
    console.error('Error deleting multiple files:', error);
    return false;
  }
}

/**
 * List all files in a directory
 */
export async function listFiles(path: string): Promise<string[]> {
  try {
    const storageRef = ref(storage, path);
    const result = await listAll(storageRef);
    
    const urlPromises = result.items.map(itemRef => getDownloadURL(itemRef));
    return await Promise.all(urlPromises);
  } catch (error) {
    console.error('Error listing files:', error);
    return [];
  }
}

/**
 * Get file metadata
 */
export async function getFileMetadata(path: string): Promise<any | null> {
  try {
    const storageRef = ref(storage, path);
    return await getMetadata(storageRef);
  } catch (error) {
    console.error('Error getting file metadata:', error);
    return null;
  }
}

/**
 * Update file metadata
 */
export async function updateFileMetadata(
  path: string,
  metadata: Record<string, any>
): Promise<boolean> {
  try {
    const storageRef = ref(storage, path);
    await updateMetadata(storageRef, metadata);
    return true;
  } catch (error) {
    console.error('Error updating file metadata:', error);
    return false;
  }
}

/**
 * Upload photo with company-specific path (matches storage.rules)
 * @param companyId - Company slug for path (e.g., "acme-oil")
 * @param wellId - Well ID
 * @param photo - Photo file to upload
 * @param onProgress - Progress callback
 */
export async function uploadPhoto(
  companyId: string,
  wellId: string,
  photo: File,
  onProgress?: (progress: UploadProgress) => void
): Promise<string | null> {
  const timestamp = Date.now();
  const path = `companies/${companyId}/wells/${wellId}/photos/${timestamp}_${photo.name}`;
  return uploadFile(path, photo, {
    customMetadata: {
      companyId,
      wellId,
      uploadedAt: new Date().toISOString()
    }
  }, onProgress);
}

/**
 * Upload invoice file with company-specific path
 * @param companyId - Company slug for path
 * @param invoiceId - Invoice ID
 * @param file - File to upload
 * @param onProgress - Progress callback
 */
export async function uploadInvoice(
  companyId: string,
  invoiceId: string,
  file: File,
  onProgress?: (progress: UploadProgress) => void
): Promise<string | null> {
  const path = `companies/${companyId}/invoices/${invoiceId}/${file.name}`;
  return uploadFile(path, file, {
    customMetadata: {
      companyId,
      invoiceId,
      uploadedAt: new Date().toISOString()
    }
  }, onProgress);
}

/**
 * Get all photos for a well
 * @param companyId - Company slug for path
 * @param wellId - Well ID
 */
export async function getWellPhotos(
  companyId: string,
  wellId: string
): Promise<string[]> {
  const path = `companies/${companyId}/wells/${wellId}/photos`;
  return listFiles(path);
}

/**
 * Delete all photos for a well
 * @param companyId - Company slug for path
 * @param wellId - Well ID
 */
export async function deleteWellPhotos(
  companyId: string,
  wellId: string
): Promise<boolean> {
  try {
    const path = `companies/${companyId}/wells/${wellId}/photos`;
    const storageRef = ref(storage, path);
    const result = await listAll(storageRef);
    
    const deletePromises = result.items.map(itemRef => deleteObject(itemRef));
    await Promise.all(deletePromises);
    return true;
  } catch (error) {
    console.error('Error deleting well photos:', error);
    return false;
  }
}

/**
 * Upload voice memo with company-specific path
 * @param companyId - Company slug for path
 * @param wellId - Well ID
 * @param audioFile - Audio file to upload
 * @param onProgress - Progress callback
 */
export async function uploadVoiceMemo(
  companyId: string,
  wellId: string,
  audioFile: File,
  onProgress?: (progress: UploadProgress) => void
): Promise<string | null> {
  const timestamp = Date.now();
  const path = `companies/${companyId}/wells/${wellId}/voice_memos/${timestamp}_${audioFile.name}`;
  return uploadFile(path, audioFile, {
    customMetadata: {
      companyId,
      wellId,
      uploadedAt: new Date().toISOString()
    }
  }, onProgress);
}

/**
 * Upload user profile photo
 * @param companyId - Company slug for path
 * @param userId - User ID
 * @param photoFile - Photo file to upload
 */
export async function uploadUserPhoto(
  companyId: string,
  userId: string,
  photoFile: File
): Promise<string | null> {
  const path = `companies/${companyId}/users/${userId}/profile.jpg`;
  return uploadFile(path, photoFile, {
    customMetadata: {
      companyId,
      userId,
      uploadedAt: new Date().toISOString()
    }
  });
}

/**
 * Upload ticket attachment
 * @param companyId - Company slug for path
 * @param ticketId - Ticket ID
 * @param file - File to upload
 * @param onProgress - Progress callback
 */
export async function uploadTicketAttachment(
  companyId: string,
  ticketId: string,
  file: File,
  onProgress?: (progress: UploadProgress) => void
): Promise<string | null> {
  const timestamp = Date.now();
  const path = `companies/${companyId}/tickets/${ticketId}/${timestamp}_${file.name}`;
  return uploadFile(path, file, {
    customMetadata: {
      companyId,
      ticketId,
      uploadedAt: new Date().toISOString()
    }
  }, onProgress);
}

/**
 * Upload production data photo
 * @param companyId - Company slug for path
 * @param wellId - Well ID
 * @param date - Production date
 * @param photoFile - Photo file to upload
 */
export async function uploadProductionPhoto(
  companyId: string,
  wellId: string,
  date: string | Date,
  photoFile: File,
  onProgress?: (progress: UploadProgress) => void
): Promise<ProductionMediaUploadResult | null> {
  const timestamp = Date.now();
  const folderPath = getProductionPhotoStoragePath(companyId, wellId, date);
  const path = `${folderPath}/${timestamp}_${photoFile.name}`;
  const metadata = {
    companyId,
    wellId,
    date: normalizeProductionDate(date),
    uploadedAt: new Date().toISOString()
  };
  const downloadUrl = await uploadFile(
    path,
    photoFile,
    { customMetadata: metadata },
    onProgress
  );

  if (!downloadUrl) {
    return null;
  }

  return { downloadUrl, storagePath: path, metadata };
}

export async function uploadProductionVoiceMemo(
  companyId: string,
  wellId: string,
  date: string | Date,
  audioFile: File,
  onProgress?: (progress: UploadProgress) => void
): Promise<ProductionMediaUploadResult | null> {
  const timestamp = Date.now();
  const folderPath = getProductionVoiceMemoStoragePath(companyId, wellId, date);
  const path = `${folderPath}/${timestamp}_${audioFile.name}`;
  const metadata = {
    companyId,
    wellId,
    date: normalizeProductionDate(date),
    uploadedAt: new Date().toISOString()
  };
  const downloadUrl = await uploadFile(
    path,
    audioFile,
    { customMetadata: metadata },
    onProgress
  );

  if (!downloadUrl) {
    return null;
  }

  return { downloadUrl, storagePath: path, metadata };
}

// ============================================
// SATELLITE IMAGE UTILITIES
// ============================================

export interface SatelliteUploadResult {
  imageUrl: string;
  downloadUrl: string;
  storagePath: string;
  size: number;
}

/**
 * Compress image to target size while maintaining quality
 */
export const compressImage = async (
  dataUrl: string, 
  targetSizeKB: number = 1500,
  format: 'image/png' | 'image/jpeg' = 'image/png'
): Promise<Blob> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('Could not get canvas context'));
        return;
      }

      // Maintain original dimensions for satellite imagery
      canvas.width = img.width;
      canvas.height = img.height;
      ctx.drawImage(img, 0, 0);

      // PNG doesn't need quality adjustment
      if (format === 'image/png') {
        canvas.toBlob((blob) => {
          if (blob) {
            resolve(blob);
          } else {
            reject(new Error('Failed to create PNG blob'));
          }
        }, format);
        return;
      }

      // For JPEG, try different quality levels to hit target size
      let quality = 0.92; // Start high for satellite imagery
      const tryCompress = () => {
        canvas.toBlob((blob) => {
          if (!blob) {
            reject(new Error('Failed to compress image'));
            return;
          }

          const sizeKB = blob.size / 1024;
          if (sizeKB <= targetSizeKB || quality <= 0.5) {
            resolve(blob);
          } else {
            quality -= 0.05;
            tryCompress();
          }
        }, format, quality);
      };
      tryCompress();
    };

    img.onerror = () => reject(new Error('Failed to load image for compression'));
    img.src = dataUrl;
  });
};

/**
 * Upload satellite image to Firebase Storage
 * Path: companies/{companyId}/{wellId}/satellite/live.png
 *
 * @param imageData - Base64 data URL of the image
 * @param wellId - Well UUID
 * @param companyId - Company slug for path
 * @param metadata - Optional custom metadata
 */
export async function uploadSatelliteImage(
  imageData: string,
  wellId: string,
  companyId: string,
  metadata?: Record<string, string>
): Promise<SatelliteUploadResult> {
  try {
    
    // ⚠️ CRITICAL: DO NOT CHANGE THIS PATH WITHOUT UPDATING STORAGE.RULES
    // The path MUST include '/wells/' to match the security rule:
    // match /companies/{companyId}/wells/{wellId}/{allPaths=**}
    const storagePath = `companies/${companyId}/wells/${wellId}/satellite/active.png`;

    // Compress image to PNG (high quality for satellite imagery)
    const compressedBlob = await compressImage(imageData, 1500, 'image/png');

    // Upload to Firebase Storage
    const imageRef = ref(storage, storagePath);
    
    const uploadMetadata = {
      contentType: 'image/png',
      customMetadata: {
        wellId,
        companyId,
        uploadedAt: new Date().toISOString(),
        ...metadata
      }
    };

    await uploadBytes(imageRef, compressedBlob, uploadMetadata);

    // Get download URL
    const downloadUrl = await getDownloadURL(imageRef);

    return {
      imageUrl: `gs://oilwellx.firebasestorage.app/${storagePath}`,
      downloadUrl,
      storagePath,
      size: compressedBlob.size
    };
  } catch (error) {
    console.error('❌ Failed to upload satellite image:', error);
    throw new Error(`Storage upload failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

/**
 * Delete satellite image from Firebase Storage
 *
 * @param wellId - Well UUID
 * @param companyId - Company slug for path
 */
export async function deleteSatelliteImage(
  wellId: string,
  companyId: string
): Promise<void> {
  try {
    const storagePath = `companies/${companyId}/wells/${wellId}/satellite/active.png`;
    const imageRef = ref(storage, storagePath);
    await deleteObject(imageRef);
  } catch (error) {
    // Don't throw if file doesn't exist
    if (error && typeof error === 'object' && 'code' in error && error.code === 'storage/object-not-found') {
      return;
    }
    console.error('❌ Failed to delete satellite image:', error);
    throw error;
  }
}

/**
 * Upload original (pre-annotation) backup image
 * Path: companies/{companyId}/{wellId}/satellite/original-{timestamp}.png
 * 
 * @param imageData - Base64 data URL of the image
 * @param wellId - Well UUID
 * @param companyId - Company slug for path
 * @param timestamp - Timestamp for unique filename
 */
export async function uploadOriginalSatelliteImage(
  imageData: string,
  wellId: string,
  companyId: string,
  timestamp: number
): Promise<string> {
  try {
    const compressedBlob = await compressImage(imageData, 1500, 'image/png');
    const storagePath = `companies/${companyId}/wells/${wellId}/satellite/original-${timestamp}.png`;
    const imageRef = ref(storage, storagePath);
    
    await uploadBytes(imageRef, compressedBlob, {
      contentType: 'image/png',
      customMetadata: {
        wellId,
        companyId,
        type: 'original',
        timestamp: timestamp.toString()
      }
    });

    const downloadUrl = await getDownloadURL(imageRef);
    return downloadUrl;
  } catch (error) {
    console.error('❌ Failed to upload original satellite image:', error);
    throw error;
  }
}

/**
 * Recursively delete all files in a storage folder
 * Used for cleanup when canceling well creation or deleting wells
 * 
 * @param folderPath - Storage path to folder (e.g., 'companies/{companyId}/{wellId}')
 */
export async function deleteStorageFolder(
  folderPath: string
): Promise<void> {
  try {
    const folderRef = ref(storage, folderPath);
    const listResult = await listAll(folderRef);

    // Delete all files in the folder
    const deletePromises = listResult.items.map((itemRef) => {
      return deleteObject(itemRef);
    });

    // Recursively delete all subfolders
    const subfolderPromises = listResult.prefixes.map((folderRef) => {
      return deleteStorageFolder(folderRef.fullPath);
    });

    await Promise.all([...deletePromises, ...subfolderPromises]);
  } catch (error) {
    // Don't throw if folder doesn't exist
    if (error && typeof error === 'object' && 'code' in error && error.code === 'storage/object-not-found') {
      return;
    }
    console.error('❌ Failed to delete storage folder:', error);
    throw error;
  }
}
